import { useAuth } from '../../contexts/AuthContext'
import { isCorrectionStage } from './corrections/correctionUtils'
import ActBadges from '../../components/ui/ActBadges'
import { useBack } from '../../navigation/navigation'
import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate, EntityLink as Link } from '../../navigation/navigation'
import api from '../../services/api'
import Icon from '../../components/Icon'
import EntityComments from '../../components/EntityComments'
import { ActionMenu, Breadcrumb, Button, StatusBadge } from '../../components/ui'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import {
  formatDateShort,
  formatMoney,
  formatPercent,
  getContractStatusVariant,
} from './stage-budget/stageBudgetUtils'
import './Company.css'

const BUDGET_STAGE_METRICS = [
  { id: 'plan', label: 'План' },
  { id: 'fact', label: 'Факт' },
  { id: 'percent', label: '%' },
]

// Показатели, которые можно включать/выключать отдельно по каждому бюджету стадии.
// «План» сюда не входит: в режиме leadingPlan он выносится в общий первый столбец.
const COLUMN_METRIC_IDS = ['fact', 'percent']

// Какие из per-column показателей реально доступны для колонки (для total «%» скрыт,
// пока showTotalPercent выключен — так же, как при рендере таблицы).
function getToggleableMetricIds(column, showTotalPercent = false) {
  return COLUMN_METRIC_IDS.filter((metricId) => (
    !(column.is_total && metricId === 'percent' && !showTotalPercent)
  ))
}

function sumBy(items, getValue) {
  return (items || []).reduce((sum, item) => sum + Number(getValue(item) || 0), 0)
}

function stageTitle(stage) {
  return [stage?.name || stage?.type || 'Стадия', stage?.object_name].filter(Boolean).join(' — ')
}

function sectionLabel(section) {
  if (section === 'production') return 'Производство'
  if (section === 'service') return 'Обслуживание'
  return ''
}

function getMetricClass(metricId, column, isFirstMetric) {
  return [
    metricId === 'plan' || metricId === 'fact' ? 'amount' : 'pct-cell',
    isFirstMetric ? 'grp-start' : '',
    metricId === 'fact' ? 'fact' : '',
    metricId === 'percent' ? 'pct' : '',
    column.is_subtotal ? 'col-subtotal' : '',
    column.is_total ? 'col-total' : '',
  ].filter(Boolean).join(' ')
}

function getBudgetColumnToneClass(columnIndex) {
  return columnIndex % 2 === 0 ? 'budget-stage-column-tone-light' : 'budget-stage-column-tone-muted'
}

function getVisibleMetricsForColumn(column, { visibleMetricIds, columnMetrics = null, leadingPlan = false, showTotalPercent = false }) {
  // Источник видимых показателей: либо per-column (columnMetrics), либо общий список.
  const metricIds = columnMetrics
    ? (columnMetrics[column.id] || [])
    : (visibleMetricIds || [])
  return BUDGET_STAGE_METRICS.filter((metric) => (
    metricIds.includes(metric.id)
    // В режиме leadingPlan «План» вынесен в общий первый столбец — из колонок убираем.
    && !(leadingPlan && metric.id === 'plan')
    && !(column.is_total && metric.id === 'percent' && !showTotalPercent)
  ))
}

function getBudgetColumnSubtitle(column) {
  const label = column?.label || ''
  const contractNumber = column?.contract_number || ''
  if (contractNumber && label.startsWith(`${contractNumber} - `)) {
    return label.slice(contractNumber.length + 3)
  }
  const distributionType = column?.distribution_type || ''
  if (isUuidLike(label) && distributionType) return distributionType
  return label
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value || '').trim())
}

function getBudgetRuleTitle(budgetRule) {
  const distributionType = budgetRule?.distribution_type || ''
  const budgetName = budgetRule?.budget_name || ''
  if (distributionType) return distributionType
  if (isUuidLike(budgetName) && distributionType) return distributionType
  if (budgetName) return budgetName
  return distributionType || `Бюджет ${budgetRule?.budget_id}`
}

function normalizeRuleOrderValue(value) {
  return String(value || '').trim().toLowerCase()
}

function getRuleName(rule) {
  return rule?.name || rule?.team || 'Без названия'
}

function getRuleCategory(rule) {
  return rule?.distribution_category || rule?.category || 'team_income'
}

function getRuleOrderKey(category, name) {
  return `${normalizeRuleOrderValue(category)}::${normalizeRuleOrderValue(name)}`
}

function buildRuleOrder(tableRows = []) {
  const order = new Map()
  const rows = tableRows || []
  rows.forEach((row, index) => {
    if (!row || row.is_total || !row.label) return
    const category = row.category || 'team_income'
    const exactKey = getRuleOrderKey(category, row.label)
    const nameKey = getRuleOrderKey('', row.label)
    if (!order.has(exactKey)) order.set(exactKey, index)
    if (!order.has(nameKey)) order.set(nameKey, index)
  })
  return order
}

function orderRulesByTableRows(rules = [], ruleOrder) {
  if (!rules?.length || !ruleOrder?.size) return rules || []
  return [...rules].sort((firstRule, secondRule) => {
    const firstName = getRuleName(firstRule)
    const secondName = getRuleName(secondRule)
    const firstOrder = ruleOrder.get(getRuleOrderKey(getRuleCategory(firstRule), firstName))
      ?? ruleOrder.get(getRuleOrderKey('', firstName))
      ?? Number.MAX_SAFE_INTEGER
    const secondOrder = ruleOrder.get(getRuleOrderKey(getRuleCategory(secondRule), secondName))
      ?? ruleOrder.get(getRuleOrderKey('', secondName))
      ?? Number.MAX_SAFE_INTEGER
    if (firstOrder !== secondOrder) return firstOrder - secondOrder
    return 0
  })
}

export function BudgetColumnsMenu({
  columns,
  columnGroups,
  visibleColumnIds,
  visibleMetricIds,
  onColumnToggle,
  onMetricToggle,
  // Режим per-column показателей (% и Факт по каждому бюджету) + общий столбец «План».
  perColumnMetrics = false,
  columnMetrics = null,
  onColumnMetricToggle,
  showPlan = true,
  onShowPlanToggle,
  showTotalPercent = false,
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  if (!columns.length) return null

  return (
    <div ref={rootRef} className={`budget-stage-columns-menu${open ? ' budget-stage-columns-menu--open' : ''}`}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="budget-stage-columns-menu__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <Icon name="table" size={14} aria-hidden="true" />
        Колонки
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={12} aria-hidden="true" />
      </Button>
      {open && (
        <div className="budget-stage-columns-menu__panel" role="region" aria-label="Колонки бюджета">
          {columnGroups?.length ? (
            columnGroups.map((group, groupIndex) => (
              <React.Fragment key={group.key}>
                {groupIndex > 0 ? <div className="budget-stage-columns-menu__divider" /> : null}
                <div className="budget-stage-columns-menu__head">{group.label}</div>
                {group.columns.map((column) => (
                  <label key={column.id} className="budget-stage-col-toggle">
                    <input
                      type="checkbox"
                      checked={visibleColumnIds.includes(column.id)}
                      onChange={() => onColumnToggle(column.id)}
                    />
                    <span>{column.is_subtotal ? `Итог ${column.label}` : (column.label || column.contract_number || 'Колонка')}</span>
                  </label>
                ))}
              </React.Fragment>
            ))
          ) : (
            <>
              <div className="budget-stage-columns-menu__head">Бюджеты стадии</div>
              {columns.map((column) => (
                <label key={column.id} className="budget-stage-col-toggle">
                  <input
                    type="checkbox"
                    checked={visibleColumnIds.includes(column.id)}
                    onChange={() => onColumnToggle(column.id)}
                  />
                  <span>{column.label || column.contract_number || 'Колонка'}</span>
                </label>
              ))}
            </>
          )}
          <div className="budget-stage-columns-menu__head budget-stage-columns-menu__head--metrics">Показатели</div>
          {perColumnMetrics ? (
            <>
              {/* Общий первый столбец «План» (итого по стадии) */}
              <label className="budget-stage-col-toggle budget-stage-metric-toggle">
                <input
                  type="checkbox"
                  checked={showPlan}
                  onChange={() => onShowPlanToggle?.()}
                />
                <span>План (итого по стадии)</span>
              </label>
              {/* % и Факт — отдельно по каждому видимому бюджету стадии */}
              {columns
                .filter((column) => visibleColumnIds.includes(column.id))
                .map((column) => {
                  const toggleable = getToggleableMetricIds(column, showTotalPercent)
                  if (!toggleable.length) return null
                  const selected = columnMetrics?.[column.id] || []
                  // Имя как в списке колонок выше (label-first), иначе бюджеты с одинаковым
                  // номером договора были бы неразличимы.
                  const columnName = column.is_total
                    ? (column.label || 'Итого по стадии')
                    : (column.label || column.contract_number || 'Колонка')
                  return (
                    <div key={column.id} className="budget-stage-col-metrics">
                      <div className="budget-stage-col-metrics__name">{columnName}</div>
                      <div className="budget-stage-col-metrics__row">
                        {toggleable.map((metricId) => {
                          const metricLabel = BUDGET_STAGE_METRICS.find((metric) => metric.id === metricId)?.label || metricId
                          return (
                            <label key={metricId} className="budget-stage-col-toggle budget-stage-metric-toggle">
                              <input
                                type="checkbox"
                                checked={selected.includes(metricId)}
                                onChange={() => onColumnMetricToggle?.(column.id, metricId)}
                              />
                              <span>{metricLabel}</span>
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
            </>
          ) : (
            BUDGET_STAGE_METRICS.map((metric) => (
              <label key={metric.id} className="budget-stage-col-toggle budget-stage-metric-toggle">
                <input
                  type="checkbox"
                  checked={visibleMetricIds.includes(metric.id)}
                  onChange={() => onMetricToggle(metric.id)}
                />
                <span>{metric.label}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export function BudgetStageTable({
  table,
  visibleColumnIds,
  visibleMetricIds,
  ariaLabel = 'Бюджет стадии',
  showTotalPercent = false,
  leadingPlan = false,
  showPlan = true,
  columnMetrics = null,
}) {
  const [expandedTeamGroups, setExpandedTeamGroups] = useState(() => new Set())
  const allColumns = table?.columns || []
  const columns = allColumns
    .filter((column) => visibleColumnIds.includes(column.id))
    .map((column) => ({
      ...column,
      metrics: getVisibleMetricsForColumn(column, { visibleMetricIds, columnMetrics, leadingPlan, showTotalPercent }),
    }))
    .filter((column) => column.metrics.length > 0)
  const rows = table?.rows || []
  // Общий первый столбец «План» (итого по стадии), вынесенный из колонок бюджетов.
  const showLeadingPlan = leadingPlan && showPlan
  if (!columns.length && !showLeadingPlan) {
    return <div className="no-data">Выберите хотя бы одну колонку и показатель бюджета стадии</div>
  }

  const toggleTeamGroup = (rowId) => {
    setExpandedTeamGroups((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  const isRowVisible = (row) => {
    if (row.row_type !== 'sub-team') return true
    return expandedTeamGroups.has(row.parent_id)
  }

  const pluralizeSubTeams = (count) => {
    const mod10 = count % 10
    const mod100 = count % 100
    if (mod10 === 1 && mod100 !== 11) return 'подкоманда'
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'подкоманды'
    return 'подкоманд'
  }

  let currentSection = null
  const tableColSpan = 1 + (showLeadingPlan ? 2 : 0) + columns.reduce((sum, column) => sum + column.metrics.length, 0)

  return (
    <div className="budget-table-wrap-compact budget-stage-table-wrap">
      <table className="budget-table-compact budget-stage-table" aria-label={ariaLabel}>
        <thead>
          <tr>
            <th rowSpan="2">Подразделение</th>
            {showLeadingPlan ? (
              <th colSpan="2" className="col-plan-lead">План</th>
            ) : null}
            {columns.map((column, columnIndex) => (
              <th
                key={column.id}
                colSpan={column.metrics.length}
                className={[
                  getBudgetColumnToneClass(columnIndex),
                  column.is_subtotal ? 'col-subtotal' : '',
                  column.is_total ? 'col-total' : '',
                ].filter(Boolean).join(' ')}
              >
                <span>{column.contract_number || column.label}</span>
                {column.contract_number
                  ? <span className="col-stage-sub">{getBudgetColumnSubtitle(column)}</span>
                  : column.sub_label
                    ? <span className="col-stage-sub">{column.sub_label}</span>
                    : null}
              </th>
            ))}
          </tr>
          <tr className="subhead">
            {showLeadingPlan ? (
              <>
                <th className="amount col-plan-lead">Сумма</th>
                <th className="pct-cell col-plan-lead">%</th>
              </>
            ) : null}
            {columns.map((column, columnIndex) => (
              column.metrics.map((metric, metricIndex) => (
                <th
                  key={`${column.id}-${metric.id}`}
                  className={[
                    getBudgetColumnToneClass(columnIndex),
                    metricIndex === 0 ? 'grp-start' : '',
                    metric.id === 'fact' ? 'metric-fact' : '',
                    metric.id === 'percent' ? 'metric-percent' : '',
                    column.is_subtotal ? 'col-subtotal' : '',
                    column.is_total ? 'col-total' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {metric.label}
                </th>
              ))
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.filter(isRowVisible).map((row) => {
            const needsSection =
              row.section &&
              row.section !== 'total' &&
              row.section !== currentSection
            if (needsSection) currentSection = row.section
            const isTeamGroup = row.row_type === 'team-group'
            const isSubTeam = row.row_type === 'sub-team'
            const isExpanded = isTeamGroup && expandedTeamGroups.has(row.id)
            const deptLabel = isTeamGroup && row.sub_team_count
              ? (
                <span className="budget-stage-dept-label">
                  <span className={`budget-stage-dept-chevron${isExpanded ? ' is-expanded' : ''}`} aria-hidden>▸</span>
                  <span>{row.label}</span>
                  <small className="budget-stage-dept-meta">{row.sub_team_count} {pluralizeSubTeams(row.sub_team_count)}</small>
                </span>
              )
              : row.label
            return (
              <React.Fragment key={row.id}>
                {needsSection && (
                  <tr className="row-section">
                    <td className="row-section-sticky">{sectionLabel(row.section)}</td>
                    {tableColSpan > 1 ? <td className="row-section-fill" colSpan={tableColSpan - 1} aria-hidden="true" /> : null}
                  </tr>
                )}
                <tr
                  className={[
                    row.is_total ? 'row-total' : 'row-budget',
                    !row.is_total && row.category ? `row-budget--${row.category}` : '',
                    isTeamGroup ? 'row-budget--team-group' : '',
                    isSubTeam ? 'row-budget--sub-team' : '',
                    row.row_type === 'section-total' ? 'row-budget--section-total' : '',
                    row.row_type === 'after-tax-total' || row.is_after_tax_total ? 'row-budget--after-tax-total' : '',
                  ].filter(Boolean).join(' ')}
                  role={isTeamGroup ? 'button' : undefined}
                  tabIndex={isTeamGroup ? 0 : undefined}
                  aria-expanded={isTeamGroup ? isExpanded : undefined}
                  aria-label={isTeamGroup ? `${isExpanded ? 'Свернуть' : 'Развернуть'} подкоманды ${row.label}` : undefined}
                  onClick={isTeamGroup ? () => toggleTeamGroup(row.id) : undefined}
                  onKeyDown={isTeamGroup ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      toggleTeamGroup(row.id)
                    }
                  } : undefined}
                >
                  <td className={`dept${isSubTeam ? ' dept--sub-team' : ''}`}>{deptLabel}</td>
                  {showLeadingPlan ? (
                    <>
                      <td className={`amount col-plan-lead ${(row.values?.total?.plan || 0) === 0 ? 'is-zero' : ''}`}>
                        {formatMoney(row.values?.total?.plan)}
                      </td>
                      <td className="pct-cell col-plan-lead">
                        {formatPercent(row.values?.total?.percent)}
                      </td>
                    </>
                  ) : null}
                  {columns.map((column, columnIndex) => {
                    const value = row.values?.[column.id] || {}
                    const columnToneClass = getBudgetColumnToneClass(columnIndex)
                    return (
                      <React.Fragment key={`${row.id}-${column.id}`}>
                        {column.metrics.map((metric, metricIndex) => {
                          const metricValue = metric.id === 'plan'
                            ? formatMoney(value.plan)
                            : metric.id === 'fact'
                              ? formatMoney(value.fact)
                              : formatPercent(value.percent)
                          return (
                            <td
                              key={`${row.id}-${column.id}-${metric.id}`}
                              className={[
                                getMetricClass(metric.id, column, metricIndex === 0),
                                columnToneClass,
                                metric.id === 'percent' && value.percent_differs_from_plan ? 'budget-stage-percent-changed' : '',
                              ].filter(Boolean).join(' ')}
                            >
                              {metricValue}
                            </td>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </tr>
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function DistributionRules({ rules, tableRows }) {
  if (!rules?.length) {
    return <div className="no-data">Правила распределения не найдены</div>
  }

  const ruleOrder = buildRuleOrder(tableRows)

  return (
    <div className="budget-stage-rules">
      {rules.map((budgetRule) => {
        const orderedRules = orderRulesByTableRows(budgetRule.rules || [], ruleOrder)
        const title = getBudgetRuleTitle(budgetRule)
        return (
          <details className="budget-stage-rule" key={budgetRule.budget_id}>
            <summary>
              <span className="rule-chevron" aria-hidden="true">▸</span>
              <span className="rule-name">{title}</span>
              <span className="rule-amount">{formatMoney(budgetRule.amount_with_vat)}</span>
              {(budgetRule.legacy_stage_id || budgetRule.stage_id || budgetRule.budget_name) && (
                <Link
                  className="budget-stage-rule-open"
                  to={`/stage/${encodeURIComponent(budgetRule.legacy_stage_id || budgetRule.stage_id || budgetRule.budget_name)}/report-preview`}
                  aria-label={`Открыть бюджет стадии ${title}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  ›
                </Link>
              )}
            </summary>
            {orderedRules.length ? (
              <div className="rule-grid">
                <div className="rg-title">Сетка распределения по подразделениям</div>
                <div className="rule-grid-inner">
                  {orderedRules.map((rule) => (
                    <div className="rg-row" key={`${budgetRule.budget_id}-${rule.id || getRuleName(rule)}`}>
                      <span>{getRuleName(rule)}</span>
                      <span>{formatPercent(rule.percentage)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="budget-stage-rule-empty">Для бюджета не настроены проценты распределения.</div>
            )}
          </details>
        )
      })}
    </div>
  )
}

function ContractList({ contracts }) {
  if (!contracts?.length) return <div className="no-data">Договоры не найдены</div>
  return (
    <div className="budget-stage-card-list">
      {contracts.map((contract) => (
        <Link className="contract-card budget-stage-link-card" key={contract.id} to={`/contract/${contract.id}`}>
          <div className="contract-card-left">
            <div className="contract-card-name">
              {contract.number || contract.id}
              <StatusBadge variant={getContractStatusVariant(contract.status)}>{contract.status || 'Без статуса'}</StatusBadge>
            </div>
            <div className="contract-card-meta">
              <span>{formatDateShort(contract.contract_date || contract.date)}</span>
            </div>
          </div>
          <div className="contract-card-right">
            <div className="contract-card-amount">{formatMoney(contract.total_amount)}</div>
          </div>
          <span className="contract-card-arrow">›</span>
        </Link>
      ))}
    </div>
  )
}

function ActList({ acts }) {
  if (!acts?.length) return <div className="no-data">Акты не найдены</div>
  return (
    <div className="budget-stage-card-list">
      {acts.map((act) => (
        <Link className="contract-card budget-stage-link-card" key={act.id} to={`/act/${act.id}`}>
          <div className="contract-card-left">
            <div className="contract-card-name">Акт · {act.number || act.id}</div>
            <div className="contract-card-meta">
              <span>{formatDateShort(act.issue_date || act.date)}</span>
              {act.contract_number && <span>{act.contract_number}</span>}
              {act.distribution_type && <span>{act.distribution_type}</span>}
            </div>
            <ActBadges act={act} />
          </div>
          <div className="contract-card-right">
            <div className="contract-card-amount">{formatMoney(act.amount)}</div>
          </div>
          <span className="contract-card-arrow">›</span>
        </Link>
      ))}
    </div>
  )
}

function BudgetCard() {
  const { user } = useAuth()
  const back = useBack('/company/budgets')
  const params = useParams()
  const stageId = params.stageId || params.id
  const navigate = useNavigate()
  usePageHeader({ title: null, hideTopHeader: true }, [])
  const [stage, setStage] = useState(null)
  const [visibleColumnIds, setVisibleColumnIds] = useState([])
  const [visibleMetricIds, setVisibleMetricIds] = useState(BUDGET_STAGE_METRICS.map((metric) => metric.id))
  // Per-column показатели (% и Факт по каждому бюджету) и видимость общего столбца «План».
  const [columnMetrics, setColumnMetrics] = useState({})
  const [showPlan, setShowPlan] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function fetchStage() {
      try {
        setLoading(true)
        setError(null)
        const response = await api.get(`/company/object-stages/${encodeURIComponent(stageId)}`)
        if (!cancelled) {
          const nextStage = response.data?.stage || null
          setStage(nextStage)
          const nextColumns = nextStage?.budget_table?.columns || []
          setVisibleColumnIds(nextColumns.map((column) => column.id))
          // По умолчанию для каждого бюджета показываем все доступные показатели (% и Факт).
          setColumnMetrics(Object.fromEntries(
            nextColumns.map((column) => [column.id, getToggleableMetricIds(column)]),
          ))
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? 'Стадия не найдена' : 'Не удалось загрузить стадию')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetchStage()
    return () => {
      cancelled = true
    }
  }, [stageId])

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  if (error || !stage) {
    return (
      <div className="company-page">
        <div className="message message-error" role="alert">{error || 'Стадия не найдена'}</div>
        <Button variant="primary" onClick={back}>К списку бюджетов</Button>
      </div>
    )
  }

  const totalActed = sumBy(stage.acts, (act) => act.amount)
  const budgetColumns = stage.budget_table?.columns || []
  const breadcrumbItems = [
    { label: 'Бюджеты', to: '/company/budgets' },
    stage.object_id ? { label: stage.object_name || 'Объект', to: `/object/${stage.object_id}` } : null,
    { label: stage.name || stage.type || 'Стадия' },
  ].filter(Boolean)

  const handleColumnToggle = (columnId) => {
    setVisibleColumnIds((prev) => (
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    ))
  }

  const handleMetricToggle = (metricId) => {
    setVisibleMetricIds((prev) => (
      prev.includes(metricId)
        ? prev.filter((id) => id !== metricId)
        : [...prev, metricId]
    ))
  }

  const handleColumnMetricToggle = (columnId, metricId) => {
    setColumnMetrics((prev) => {
      const current = prev[columnId] || []
      const next = current.includes(metricId)
        ? current.filter((id) => id !== metricId)
        : [...current, metricId]
      return { ...prev, [columnId]: next }
    })
  }

  return (
    <div className="company-page budget-detail-page budget-stage-page">
      <Breadcrumb items={breadcrumbItems} />

      <header className="budget-detail-header budget-stage-header">
        <div>
          <div className="budget-stage-title-row">
            <Button
              variant="ghost"
              size="sm"
              className="budget-stage-back-button"
              onClick={back}
              aria-label="Назад к бюджетам"
            >
              <span aria-hidden="true">←</span>
            </Button>
            <h1>{stageTitle(stage)}</h1>
            <StatusBadge variant={getContractStatusVariant(stage.status)}>{stage.status || 'Без статуса'}</StatusBadge>
          </div>
          <p className="budget-stage-subtitle">
            {[stage.object_name && `Объект ${stage.object_name}`, stage.client_name].filter(Boolean).join(' · ')}
          </p>
        </div>
        {user?.permissions?.['stages.create'] && isCorrectionStage(stage) && <Button variant="primary" onClick={() => navigate(`/company/budgets/${encodeURIComponent(stage.id)}/corrections/new`)}>+ Создать новую корректировку</Button>}
        <ActionMenu
          label="Действия"
          variant="secondary"
          items={[
            ...(user?.permissions?.['stages.create'] && isCorrectionStage(stage) ? [{id:'create-correction',label:'Создать новую корректировку',onSelect:()=>navigate(`/company/budgets/${encodeURIComponent(stage.id)}/corrections/new`)}] : []),
            {
              id: 'edit-stage',
              label: 'Редактировать стадию',
              icon: <Icon name="edit" size={14} />,
              onSelect: () => navigate(`/company/budgets/${encodeURIComponent(stage.public_id || stage.id)}/edit`),
            },
          ]}
        />
      </header>

      {(stage.budgets || []).some(b => b.custom_data?.correction) && <section className="card budget-stage-section"><div className="card-header"><h2 className="section-title">Корректировки</h2></div><div className="budget-stage-link-list">{stage.budgets.filter(b=>b.custom_data?.correction).map(b=><Link key={b.id} className="contract-card" to={`/company/corrections/${encodeURIComponent(b.legacy_stage_id)}`}><span>{b.name}</span><strong>{formatMoney(b.amount_with_vat)}</strong></Link>)}</div></section>}

      <section className="card budget-stage-section">
        <div className="card-header budget-stage-card-header">
          <h2 className="section-title">Бюджет стадии</h2>
          <BudgetColumnsMenu
            columns={budgetColumns}
            visibleColumnIds={visibleColumnIds}
            visibleMetricIds={visibleMetricIds}
            onColumnToggle={handleColumnToggle}
            onMetricToggle={handleMetricToggle}
            perColumnMetrics
            columnMetrics={columnMetrics}
            onColumnMetricToggle={handleColumnMetricToggle}
            showPlan={showPlan}
            onShowPlanToggle={() => setShowPlan((value) => !value)}
          />
        </div>
        <BudgetStageTable
          table={stage.budget_table}
          visibleColumnIds={visibleColumnIds}
          visibleMetricIds={visibleMetricIds}
          leadingPlan
          showPlan={showPlan}
          columnMetrics={columnMetrics}
        />
      </section>

      <section className="card budget-stage-section">
        <div className="card-header">
          <h2 className="section-title">Правила распределения</h2>
        </div>
        <DistributionRules rules={stage.distribution_rules || []} tableRows={stage.budget_table?.rows || []} />
      </section>

      <div className="object-cols budget-stage-columns">
        <section className="card budget-stage-section">
          <div className="card-header">
            <h2 className="section-title">Договоры</h2>
            <div className="budget-stage-section-note">по этой стадии</div>
          </div>
          <ContractList contracts={stage.contracts || []} />
        </section>
        <section className="card budget-stage-section">
          <div className="card-header">
            <h2 className="section-title">Акты</h2>
            <div className="budget-stage-section-note">заактировано {formatMoney(totalActed)}</div>
          </div>
          <ActList acts={stage.acts || []} />
        </section>
      </div>

      <EntityComments entityType="budgets" entityId={stageId} />
    </div>
  )
}

export default BudgetCard
